import { Col, Typography } from 'antd';
import config from 'config';
import { format, addDays } from 'date-fns';
import {
  DonorType,
  useGetDonorLivesSavedLazyQuery,
  DonorOutput,
  useGetDonorQuery,
  PermissionsEnum,
} from 'gql/types';
import { FormItem, Input } from 'modules/common/components';
import { useHidingFields } from 'modules/common/hooks';
import styles from 'modules/donor/components/EditDonorForm/styles.module.scss';
import { downloadFileHandler } from 'modules/donor/utils/download-file-handler';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { envServices } from 'utils/EnvServices';

import { getFields, OptionalFormSectionField } from './fields';

type PropTypes = {
  fieldNamePrefix: string;
  disabled?: boolean;
  donor?: DonorOutput;
};

const OptionalFormSection: FC<PropTypes> = ({
  fieldNamePrefix,
  disabled,
  donor,
}) => {
  const { t } = useTranslation('donor.EditDonorForm.OptionalSection');

  const pageParams = useParams();
  const shortDonorId = pageParams.id || '';

  const { showField, hideField, hiddenFields } =
    useHidingFields<OptionalFormSectionField>({
      hiddenFields: [OptionalFormSectionField.Team],
    });

  const ownerId = donor?.user?.id;

  const {
    control,
    formState,
    watch,
    trigger,
    setValue,
    getValues,
    setError,
    clearErrors,
    getFieldState,
  } = useFormContext();

  const donorSpecie = useWatch({
    control,
    name: 'required.species',
  });

  const optionalFormSectionFields = useMemo(
    () => getFields({ donorSpecie, hiddenFields, ownerId: ownerId }),
    [donorSpecie, hiddenFields, ownerId]
  );
  const currentUser = useCurrentUser();
  const donorTypeFieldName = `${fieldNamePrefix}.${OptionalFormSectionField.DonorType}`;
  const donorAttendDaycareDaysFieldName = `${fieldNamePrefix}.${OptionalFormSectionField.AttendDaycareDays}`;
  const donorType = watch(donorTypeFieldName) as DonorType;
  const wontRegularlyAttendDaycare = watch(
    `${fieldNamePrefix}.${OptionalFormSectionField.WontRegularlyAttendDaycare}`
  );

  const [getDonorLivesSaved, getDonorLivesSavedQuery] =
    useGetDonorLivesSavedLazyQuery();

  useGetDonorQuery({
    variables: { input: { id: shortDonorId } },
    onCompleted(data) {
      if (data?.getDonor.id) {
        getDonorLivesSaved({
          variables: {
            input: {
              donorId: data?.getDonor.id,
            },
          },
        });
      }
    },
  });

  useEffect(() => {
    const { Team, IsDaycare } = OptionalFormSectionField;
    if (donorType === DonorType.Individual) {
      showField(IsDaycare);
      hideField(Team);
    } else if (donorType === DonorType.Grouped) {
      showField(Team);
      hideField(IsDaycare);
    }
    trigger();
  }, [donorType]);

  useEffect(() => {
    const currentWontRegularlyAttend = getValues(
      `${fieldNamePrefix}.${OptionalFormSectionField.WontRegularlyAttendDaycare}`
    );
    const currentAttendDays = getValues(
      `${fieldNamePrefix}.${OptionalFormSectionField.AttendDaycareDays}`
    );

    if (currentWontRegularlyAttend) {
      setValue(donorAttendDaycareDaysFieldName, []);
    }

    if (
      !currentWontRegularlyAttend &&
      (!currentAttendDays || !currentAttendDays.length)
    ) {
      setError(
        `${fieldNamePrefix}.${OptionalFormSectionField.AttendDaycareDays}`,
        {
          message: t('attendDaycareDaysValidationError'),
        }
      );
    } else {
      clearErrors(
        `${fieldNamePrefix}.${OptionalFormSectionField.AttendDaycareDays}`
      );
    }
  }, [wontRegularlyAttendDaycare]);

  return (
    <>
      {optionalFormSectionFields.map(({ Component, label, name }) => {
        const fieldName = `${fieldNamePrefix}.${name}`;

        const { error } = getFieldState(fieldName, formState);

        const petWithoutOwnerTeamError =
          name === OptionalFormSectionField.Team && !ownerId;

        const errorMessage = petWithoutOwnerTeamError
          ? t('cantChooseTeamForDonorWithoutOwner')
          : error?.message;

        const isAttendDaysSelectDisabled =
          name === OptionalFormSectionField.AttendDaycareDays &&
          wontRegularlyAttendDaycare;

        const isDisabledApproveML =
          name === OptionalFormSectionField.ApprovedDonationML &&
          !currentUser.isCan(PermissionsEnum.ApproveDonationMl);

        const isFieldDisabled =
          isAttendDaysSelectDisabled || isDisabledApproveML || disabled;

        return (
          <Col span={24} key={fieldName}>
            <FormItem
              label={label}
              extra={errorMessage}
              validateStatus={
                !petWithoutOwnerTeamError && errorMessage ? 'error' : undefined
              }
            >
              <Component
                control={control}
                name={fieldName}
                disabled={isFieldDisabled}
              />
            </FormItem>
          </Col>
        );
      })}
      {donor?.lastLabworkFile && (
        <Col span={24}>
          <FormItem className={styles.flatFormItem} label={t('labworkResults')}>
            {donor.lastLabworkFile.length > 0
              ? donor.lastLabworkFile.map((file) => (
                  <div key={file.id}>
                    <Typography.Link
                      download
                      onClick={async () =>
                        downloadFileHandler(
                          file.filename,
                          `${envServices.get('SERVER_URI')}/labwork/files/${
                            file.id
                          }`
                        )
                      }
                    >
                      {file?.filename}
                    </Typography.Link>
                    <br />
                  </div>
                ))
              : t('noResults')}
          </FormItem>
        </Col>
      )}
      {donor?.lastLabworkDate && (
        <Col span={24}>
          <FormItem className={styles.flatFormItem} label={t('nextLabwork')}>
            {format(
              addDays(
                new Date(donor?.lastLabworkDate),
                config.VALIDITY_PERIODS.LABWORK_DURATION_DAYS
              ),
              config.DATE_FORMATS.DATE
            )}
          </FormItem>
        </Col>
      )}
      <Col span={24}>
        <FormItem className={styles.flatFormItem} label={t('livesSavedRatio')}>
          <Input
            value={getDonorLivesSavedQuery?.data?.getDonorLivesSaved.lives}
            disabled={true}
            addonAfter={t('lives').toUpperCase()}
          />
        </FormItem>
      </Col>
    </>
  );
};

export default OptionalFormSection;
